import { motion } from "framer-motion";
import { MusicNote, Trash, Clock, Metronome, Waveform } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export const MusicGrid = ({ tracks, onDelete }) => {
  if (tracks.length === 0) {
    return (
      <div className="text-center py-16" data-testid="no-tracks-message">
        <Waveform size={64} className="mx-auto mb-4 text-neutral-700" />
        <p className="text-muted-foreground text-lg mb-1">No tracks yet</p>
        <p className="text-muted-foreground text-sm">Generate your first AI music track above</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="music-tracks-grid">
      {tracks.map((track, idx) => (
        <motion.div
          key={track.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.05 }}
          className="border border-border bg-surface p-5 hover:-translate-y-1 transition-all group"
          data-testid={`track-card-${track.id}`}
        >
          {/* Track header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-800 flex items-center justify-center">
                <MusicNote size={20} weight="fill" className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-tight" data-testid={`track-title-${track.id}`}>
                  {track.title}
                </h3>
                <p className="text-xs text-muted-foreground">{track.genre} / {track.mood}</p>
              </div>
            </div>
            <Button
              onClick={() => onDelete(track.id)}
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-white"
              data-testid={`delete-track-${track.id}`}
            >
              <Trash size={16} />
            </Button>
          </div>

          {/* Prompt */}
          <p className="text-xs text-foreground/70 mb-3 line-clamp-2">{track.prompt}</p>

          {/* Waveform visualization (decorative) */}
          <div className="h-12 flex items-end gap-[2px] mb-3" data-testid={`track-waveform-${track.id}`}>
            {Array.from({ length: 40 }).map((_, i) => {
              const h = Math.sin(i * 0.5 + idx) * 30 + 35;
              return (
                <div
                  key={`bar-${i}`}
                  className="flex-1 bg-gradient-to-t from-purple-600/60 to-indigo-500/40 rounded-t-sm"
                  style={{ height: `${h}%` }}
                />
              );
            })}
          </div>

          {/* Track metadata */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {track.duration}s
            </span>
            <span className="flex items-center gap-1">
              <Metronome size={14} />
              {track.bpm} BPM
            </span>
          </div>

          {/* Instruments */}
          <div className="flex flex-wrap gap-1 mt-3">
            {track.instruments?.map((inst) => (
              <span
                key={inst}
                className="text-[10px] px-2 py-0.5 border border-border/50 text-muted-foreground"
              >
                {inst}
              </span>
            ))}
          </div>

          {/* Mock badge */}
          <div className="mt-3 pt-3 border-t border-border/30">
            <span className="text-[10px] uppercase tracking-wider text-yellow-600 font-semibold">
              Mock - API integration pending
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );
};
